import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchServiceCatalog } from '../services/serviceApi';

const ServiceContext = createContext();

export function ServiceProvider({ children }) {
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentBooking, setCurrentBooking] = useState(null);

  // Load service catalog on mount
  useEffect(() => {
    // eslint-disable-next-line no-use-before-define -- pre-existing: the reference is inside a deferred callback, so it resolves after the declaration (#1206 enabled this rule)
    loadServiceCatalog();
  }, []);

  const loadServiceCatalog = async () => {
    try {
      setLoading(true);
      setError(null);
      const catalog = await fetchServiceCatalog();
      setServiceCatalog(catalog);
      console.log('[ServiceContext] Service catalog loaded:', catalog.length, 'services');
    } catch (err) {
      console.error('[ServiceContext] Failed to load service catalog:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get service by ID
   */
  const getServiceById = (serviceId) => {
    return serviceCatalog.find(s => s.serviceId === serviceId);
  };

  /**
   * Get services by type
   */
  const getServicesByType = (serviceType) => {
    return serviceCatalog.filter(s => s.serviceType === serviceType);
  };

  /**
   * Start a new booking session
   */
  const startBooking = (initialData = {}) => {
    setCurrentBooking({
      step: 'identification',
      data: initialData,
      timestamp: Date.now()
    });
    console.log('[ServiceContext] Booking started:', initialData);
  };

  /**
   * Update current booking data
   */
  const updateBooking = (newData) => {
    setCurrentBooking(prev => {
      if (!prev) return null;
      return {
        ...prev,
        data: { ...prev.data, ...newData },
        timestamp: Date.now()
      };
    });
  };

  /**
   * Move to next step in booking
   */
  const setBookingStep = (step) => {
    setCurrentBooking(prev => {
      if (!prev) return null;
      return {
        ...prev,
        step,
        timestamp: Date.now()
      };
    });
  };

  /**
   * Complete booking and reset
   */
  const completeBooking = (appointmentData) => {
    console.log('[ServiceContext] Booking completed:', appointmentData);
    setCurrentBooking({
      step: 'completed',
      data: appointmentData,
      timestamp: Date.now()
    });

    // Auto-reset after 30 seconds
    setTimeout(() => {
      setCurrentBooking(null);
    }, 30000);
  };

  /**
   * Cancel/Reset booking
   */
  const resetBooking = () => {
    setCurrentBooking(null);
    console.log('[ServiceContext] Booking reset');
  };

  const value = {
    // Service catalog
    serviceCatalog,
    loading,
    error,
    getServiceById,
    getServicesByType,
    reloadCatalog: loadServiceCatalog,

    // Booking state
    currentBooking,
    startBooking,
    updateBooking,
    setBookingStep,
    completeBooking,
    resetBooking
  };

  return (
    <ServiceContext.Provider value={value}>
      {children}
    </ServiceContext.Provider>
  );
}

export function useService() {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error('useService must be used within ServiceProvider');
  }
  return context;
}
